import React from 'react';
import styled from 'styled-components';
import { themeColor } from '../Theming';
import { blueLightCardImages } from '../Theming/images';
import {
  ANALYTICS_PAGE_NAMES,
  trackClickedActionEvent,
  useIsIPad,
} from '../Common/Analytics/utils';
import usePageViewTracking from '../Common/Hooks/usePageViewTracking';
import { isAndroid } from 'react-device-detect';
import { useHistory } from 'react-router-dom';
import { useQuery } from '../Common/Hooks';
import { Path } from '../Routing';
import { onClose } from '../TIM/KindredAndroid';
import Header from './components/Header';
import { BoldText } from './BlueLightCardPluginActivationAlwaysAllow';

const Container = styled.div<{ isIPad?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100dvh;
  width: ${(props) => (props.isIPad ? '100%' : '100vw')};
  max-width: ${(props) => (props.isIPad ? '100%' : '600px')};
  margin: 0 auto;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
`;

const MainContent = styled.div<{ isIPad?: boolean }>`
  padding: ${(props) =>
    props.isIPad ? '200px 100px 0 100px' : '50px 24px 0px 24px'};
  overflow-y: auto;

  & > p {
    font-size: ${(props) => (props.isIPad ? '28px' : '22px')};
    font-weight: 600;
    & > span {
      display: inline-block;
    }
  }
`;

const List = styled.ul<{ isIPad?: boolean }>`
  list-style: none;
  padding: 0 0 0 ${(props) => (props.isIPad ? '16px' : '5px')};
  margin: 0;

  & > li {
    display: flex;
    align-items: flex-start;
    margin-bottom: ${(props) => (props.isIPad ? '40px' : '32px')};
  }
`;

const ImageContainer = styled.div<{ isIPad?: boolean }>`
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: ${(props) => (props.isIPad ? '20px' : '16px')};

  & > img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const ListContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ListTitle = styled.h3<{ isIPad?: boolean }>`
  font-family: 'Museo Sans', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0%;
  vertical-align: middle;
  margin: 0;
  padding: 0;
  color: ${themeColor('black')};
`;

const ListDescription = styled.p<{ isIPad?: boolean }>`
  font-family: 'Museo Sans', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0%;
  vertical-align: middle;
  margin: 0;
  padding: 0;
  color: ${themeColor('black')};
  opacity: 0.8;
`;

const Button = styled.button<{ isIPad?: boolean }>`
  font-family: 'Museo Sans', sans-serif;
  background-color: ${themeColor('blueLightCardPrimary')};
  width: 336px;
  height: 48px;
  border-radius: 4px;
  opacity: 1;
  gap: 8px;
  padding: 8px 24px;
  color: ${themeColor('white')};
  font-size: ${(props) => (props.isIPad ? '24px' : '18px')};
  font-weight: 600;
  font-style: semi-bold;
  border: none;
  cursor: pointer;
`;

const TextLink = styled.a<{ isIPad?: boolean }>`
  font-family: 'Museo Sans', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0%;
  text-align: center;
  color: ${themeColor('blueLightCardPrimary')};
  text-decoration: none;
  cursor: pointer;
  margin-top: 16px;

  &:hover {
    opacity: 0.8;
  }
`;

const BottomContent = styled.div<{ isIPad?: boolean }>`
  padding: ${(props) =>
    props.isIPad ? '0 100px 48px 100px' : '0 24px 24px 24px'};
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
`;

const BlueLightCardActivation = () => {
  const { landing_page } = ANALYTICS_PAGE_NAMES;
  usePageViewTracking(landing_page, 'BlueLightCard');
  const history = useHistory();
  const query = useQuery();
  const origin = query.get('origin');

  const isIPad = useIsIPad();

  const steps = [
    {
      title: 'Get notified of discounts',
      description:
        'Our Deal Finder checks for discounts when you’re shopping online.',
      img: blueLightCardImages.get_notifications,
    },
    {
      title: 'Shop anywhere',
      description:
        'If there’s a Blue Light Card discount available, our Deal Finder will pop up.',
      img: blueLightCardImages.shop_anywhere,
    },
    {
      title: 'Discounts in seconds',
      description: "It's quick, easy and completely free!",
      img: blueLightCardImages.get_discounts,
    },
  ];

  const onActivateClick = () => {
    trackClickedActionEvent(query, 'BlueLightCard', 'activate', landing_page);
    if (isAndroid) {
      return history.push({
        pathname: Path.BlueLightCardPluginAndroidAccessibility,
        search: location.search,
      });
    }

    history.push({
      pathname: Path.BlueLightCardPluginActivationMultiStep,
      search: location.search,
    });
  };

  const openBlueLightCardApp = () => {
    if (isAndroid) {
      return onClose();
    }
    window.location.href = `${origin}://`;
  };

  return (
    <Container isIPad={isIPad}>
      <Header
        showBrands
        starTopPosition={isIPad ? '100%' : '92%'}
        height={isIPad ? 230 : 150}
        heading={
          <>
            <BoldText>Your discounts</BoldText>
            <BoldText>made effortless</BoldText>
          </>
        }
        subtitle={
          <p>
            Our Deal Finder works quietly in the background, helping you save
            time and money.
          </p>
        }
      />
      <MainContent isIPad={isIPad}>
        <List>
          {steps.map((step, index) => (
            <li key={index}>
              <ImageContainer isIPad={isIPad}>
                <img src={step.img.default} alt={step.title} />
              </ImageContainer>
              <ListContent>
                <ListTitle isIPad={isIPad}>{step.title}</ListTitle>
                <ListDescription isIPad={isIPad}>
                  {step.description}
                </ListDescription>
              </ListContent>
            </li>
          ))}
        </List>
      </MainContent>
      <BottomContent isIPad={isIPad}>
        <Button isIPad={isIPad} onClick={onActivateClick}>
          Get our Deal Finder now
        </Button>
        <TextLink isIPad={isIPad} onClick={openBlueLightCardApp}>
          Already activated?
        </TextLink>
      </BottomContent>
    </Container>
  );
};

export default BlueLightCardActivation;
